import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { authSelectors } from '../../../../store/selectors/auth';
import { checkLessonVideo, sendLessonVideo } from '../../../../api/programs';
import usePreviewFeature from '../../../../hooks/usePreviewFeature';
import './lesson-video-tile.scss';

const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20.7 4.3 3.4 11c-1.2.5-1.2 1.2-.2 1.5l4.4 1.4 1.7 5.2c.2.6.1.8.7.8.5 0 .7-.2 1-.5l2.1-2.1 4.5 3.3c.8.5 1.4.2 1.6-.8l2.9-13.8c.3-1.2-.4-1.8-1.4-1.3Zm-3 3.4-8.3 7.5-.3 3.5-1.4-4.6 9.2-5.8c.4-.3.8-.1.8-.6Z" style={{ fill: 'currentColor', stroke: 'none' }} />
  </svg>
);

TelegramIcon.propTypes = { className: PropTypes.string };
TelegramIcon.defaultProps = { className: '' };

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6 6 18" style={{ fill: 'none' }} />
  </svg>
);

const STEP_CHECK = 'check';
const STEP_AUTH = 'auth';
const STEP_PARTS = 'parts';
const STEP_CONFIRM = 'confirm';
const STEP_SENDING = 'sending';
const STEP_DONE = 'done';
const STEP_ERROR = 'error';

const LessonVideoTile = ({ lesson }) => {
  const isEnabled = usePreviewFeature('lesson-telegram-video');
  const isPartsEnabled = usePreviewFeature('lesson-video-parts');
  const auth = useSelector(authSelectors);
  const [step, setStep] = useState(null);
  const [message, setMessage] = useState('');
  const [botUrl, setBotUrl] = useState('');
  const [part, setPart] = useState(0);
  const requestRef = useRef(0);

  const isOpen = step !== null;
  const isSending = step === STEP_SENDING;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !isSending) {
        requestRef.current += 1;
        setStep(null);
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isSending]);

  if (!isEnabled || !(lesson.group_video_count > 0)) {
    return null;
  }

  const lessonName = lesson.title || lesson.topic || lesson.program_title || lesson.name || 'заняття';
  const partsCount = isPartsEnabled ? Number(lesson.group_video_count) || 0 : 0;
  const hasParts = partsCount > 1;
  const parts = hasParts ? Array.from({ length: partsCount }, (_, index) => index + 1) : [];

  const close = () => {
    if (isSending) return;
    requestRef.current += 1;
    setStep(null);
  };

  const check = async () => {
    requestRef.current += 1;
    const request = requestRef.current;
    setStep(STEP_CHECK);

    try {
      const response = await checkLessonVideo({ eventId: lesson.group_video_event_id || 0, token: auth.token });
      if (request !== requestRef.current) return;
      setBotUrl(response?.data?.data?.bot_url || '');
      setStep(hasParts ? STEP_PARTS : STEP_CONFIRM);
    } catch (error) {
      if (request !== requestRef.current) return;
      const data = error?.response?.data;
      setMessage(data?.info?.message || 'Не вдалося перевірити запис. Перевірте інтернет і спробуйте ще раз.');
      setBotUrl(data?.data?.bot_url || '');
      setStep(data?.data?.need_auth ? STEP_AUTH : STEP_ERROR);
    }
  };

  const openModal = () => {
    setPart(0);
    setMessage('');
    check();
  };

  const choosePart = (number) => {
    setPart(number);
    setStep(STEP_CONFIRM);
  };

  const send = async () => {
    setStep(STEP_SENDING);

    try {
      const response = await sendLessonVideo({
        eventId: lesson.group_video_event_id || 0,
        part: hasParts ? part : 0,
        token: auth.token,
      });
      setMessage(response?.data?.info?.message || 'Готово! Запис уже в нашому чаті-турботи.');
      setBotUrl(response?.data?.data?.bot_url || '');
      setStep(STEP_DONE);
    } catch (error) {
      const data = error?.response?.data;
      setMessage(data?.info?.message || 'Не вдалося надіслати запис. Перевірте інтернет і спробуйте ще раз.');
      setBotUrl(data?.data?.bot_url || '');
      setStep(data?.data?.need_auth ? STEP_AUTH : STEP_ERROR);
    }
  };

  return (
    <>
      <button
        type="button"
        className="course-tile course-tile--tg"
        onClick={openModal}
        title="Отримати запис заняття в Telegram"
      >
        <TelegramIcon className="course-tile__icon" />
        <span className="course-tile__label">Запис у TG</span>
      </button>

      {isOpen && createPortal(
        <div
          className="lvt-overlay"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <div className="lvt-modal" role="dialog" aria-modal="true" aria-labelledby="lvt-modal-title">
            <div className="lvt-modal__head">
              <span className="lvt-modal__icon"><TelegramIcon /></span>
              <h2 className="lvt-modal__title" id="lvt-modal-title">Запис заняття</h2>
              <button type="button" className="lvt-modal__close" aria-label="Закрити" onClick={close} disabled={isSending}>
                <CloseIcon />
              </button>
            </div>

            {step === STEP_CHECK && (
              <p className="lvt-modal__text">Готуємо запис…</p>
            )}

            {step === STEP_AUTH && (
              <>
                <p className="lvt-modal__text lvt-modal__text--error">{message}</p>
                <div className="lvt-modal__actions">
                  {botUrl && (
                    <a className="lvt-btn lvt-btn--main" href={botUrl} target="_blank" rel="noopener noreferrer">
                      Відкрити чат-турботи
                    </a>
                  )}
                  <button type="button" className={`lvt-btn ${botUrl ? '' : 'lvt-btn--main'}`} onClick={check}>
                    Я авторизувався
                  </button>
                  <button type="button" className="lvt-btn" onClick={close}>
                    Закрити
                  </button>
                </div>
              </>
            )}

            {step === STEP_PARTS && (
              <>
                <p className="lvt-modal__text">
                  Запис «<b>{lessonName}</b>» складається з {partsCount} частин. Оберіть, яку отримати в наш чат-турботи.
                </p>
                <div className="lvt-parts">
                  {parts.map((number) => (
                    <button key={number} type="button" className="lvt-part" onClick={() => choosePart(number)}>
                      <span className="lvt-part__num">{number}</span>
                      <span className="lvt-part__label">Частина {number}</span>
                    </button>
                  ))}
                </div>
                <div className="lvt-modal__actions">
                  <button type="button" className="lvt-btn" onClick={close}>
                    Скасувати
                  </button>
                </div>
              </>
            )}

            {(step === STEP_CONFIRM || step === STEP_SENDING) && (
              <>
                <p className="lvt-modal__text">
                  {hasParts ? <>Частину <b>{part} з {partsCount}</b> запису </> : 'Запис '}«<b>{lessonName}</b>»
                  {lesson.event_date ? ` від ${lesson.event_date}` : ''} Ви отримаєте в наш чат-турботи.
                </p>
                <div className="lvt-modal__actions">
                  <button type="button" className="lvt-btn lvt-btn--main" onClick={send} disabled={isSending}>
                    {isSending ? 'Надсилаємо…' : 'Отримати запис'}
                  </button>
                  <button type="button" className="lvt-btn" onClick={close} disabled={isSending}>
                    Скасувати
                  </button>
                </div>
              </>
            )}

            {(step === STEP_DONE || step === STEP_ERROR) && (
              <>
                <p className={`lvt-modal__text ${step === STEP_ERROR ? 'lvt-modal__text--error' : 'lvt-modal__text--ok'}`}>
                  {message}
                </p>
                <div className="lvt-modal__actions">
                  {botUrl && (
                    <a className="lvt-btn lvt-btn--main" href={botUrl} target="_blank" rel="noopener noreferrer">
                      Відкрити чат-турботи
                    </a>
                  )}
                  {step === STEP_ERROR && (
                    <button type="button" className={`lvt-btn ${botUrl ? '' : 'lvt-btn--main'}`} onClick={send}>
                      Спробувати ще раз
                    </button>
                  )}
                  {step === STEP_DONE && hasParts && (
                    <button type="button" className="lvt-btn" onClick={() => setStep(STEP_PARTS)}>
                      Інша частина
                    </button>
                  )}
                  <button type="button" className="lvt-btn" onClick={close}>
                    Закрити
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

LessonVideoTile.propTypes = {
  lesson: PropTypes.object.isRequired,
};

export default LessonVideoTile;
